import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sword, ShieldCheck, Feather, BookOpen, Music, Check, X, ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { defaultAppearance } from "@/components/CharacterCreator";
import { classChibi } from "@/lib/classChibi";

/**
 * Two-step mandatory onboarding wizard shown before any gameplay when
 * `character.onboarded === false`. Step 1 forces a moderator-approved
 * unique username, step 2 forces a class pick.
 *
 * Emits `onDone(character)` when the character is finalised so the parent
 * Game view can hot-swap to the freshly-onboarded state.
 */

const CLASSES = [
  {
    id: "Warrior",
    Icon: Sword,
    tint: "#8A0303",
    tagline: "Front-line berserker",
    stats: { strength: 11, defense: 7, dexterity: 6, intelligence: 5, charisma: 5, concentration: 6 },
    hp: 34,
    blurb: "Extra Strength for raw damage. Vigor pool for special strikes.",
  },
  {
    id: "Rogue",
    Icon: Feather,
    tint: "#4A7BC7",
    tagline: "Silent knife",
    stats: { strength: 6, defense: 5, dexterity: 11, intelligence: 6, charisma: 6, concentration: 6 },
    hp: 28,
    blurb: "Extra Dexterity for crits and dodge. Vigor pool for special strikes.",
  },
  {
    id: "Guardian",
    Icon: ShieldCheck,
    tint: "#B89C3A",
    tagline: "Bulwark of iron",
    stats: { strength: 6, defense: 11, dexterity: 5, intelligence: 5, charisma: 7, concentration: 6 },
    hp: 38,
    blurb: "Extra Defense and HP. UNIQUELY has both Mana AND Vigor pools.",
  },
  {
    id: "Wizard",
    Icon: BookOpen,
    tint: "#E55A00",
    tagline: "Occult scholar",
    stats: { strength: 4, defense: 5, dexterity: 5, intelligence: 12, charisma: 6, concentration: 8 },
    hp: 26,
    blurb: "Extra Intelligence for spells. Mana pool + boosted Concentration.",
  },
  {
    id: "Bard",
    Icon: Music,
    tint: "#B44BE0",
    tagline: "Loremaster & haggler",
    stats: { strength: 5, defense: 5, dexterity: 7, intelligence: 7, charisma: 10, concentration: 6 },
    hp: 30,
    blurb: "Extra Charisma for prices. Mana pool for support spells.",
  },
];

const SKILL_HINTS = [
  { icon: "🌲", label: "Woodcutting → +2 HP every 2 lv (cap +40)" },
  { icon: "⛏", label: "Mining → +1 Defense every 3 lv (cap +12)" },
  { icon: "🎣", label: "Fishing → +1 Dexterity every 3 lv" },
  { icon: "🍄", label: "Gathering → +1 Intelligence every 3 lv" },
  { icon: "🔨", label: "Crafting → +1 Charisma every 3 lv" },
];

export default function Onboarding({ onDone }) {
  const [step, setStep] = useState("name"); // 'name' | 'appearance' | 'class'
  const [name, setName] = useState("");
  const [nameChecked, setNameChecked] = useState(null); // null | true | false
  const [nameReason, setNameReason] = useState("");
  const [checking, setChecking] = useState(false);
  const [appearance, setAppearance] = useState(defaultAppearance());
  const [chosen, setChosen] = useState(null);
  const [extraClasses, setExtraClasses] = useState([]);

  // Pull GM-tool classes to expose any custom classes the admin created
  useEffect(() => {
    api.gmClasses().then(r => {
      const built = (r?.classes || []).map(c => ({
        id: c.name,
        Icon: Sword,
        tint: "#B89C3A",
        tagline: c.role || "",
        stats: c.stats_snapshot || { strength: 8, defense: 8, dexterity: 8, intelligence: 8, charisma: 8, concentration: 8 },
        hp: (c.stats_snapshot && c.stats_snapshot.hp) || 30,
        blurb: c.description || c.class_bonus || "",
        image: c.image || null,
        animations: c.animations || [],
        _gm: true,
      })).filter(c => !CLASSES.find(x => x.id === c.id));
      setExtraClasses(built);
    }).catch(() => {});
  }, []);

  // Merged catalog: builtin + GM custom (dedup by id)
  const CLASSES_MERGED = [...CLASSES, ...extraClasses];
  const [busy, setBusy] = useState(false);

  const checkName = async () => {
    if (!name.trim() || checking) return;
    setChecking(true);
    setNameChecked(null);
    setNameReason("");
    try {
      const r = await api.onboardCheckName(name.trim());
      setNameChecked(!!r.ok);
      if (!r.ok) setNameReason(r.reason || "Name not allowed.");
    } catch (e) {
      setNameChecked(false);
      setNameReason(e?.response?.data?.detail || "Check failed. Try again.");
    } finally {
      setChecking(false);
    }
  };

  const commit = async () => {
    if (!name.trim() || !chosen || busy) return;
    setBusy(true);
    try {
      const ch = await api.onboard(name.trim(), chosen, appearance);
      // Persist visual FX preferences set during onboarding (spec 37-38).
      const vfx = typeof window !== "undefined" ? window.__gw_visual_prefs : null;
      if (vfx) {
        api.setPreferences({ visual_effects: vfx }).catch(() => { /* non-blocking */ });
      }
      toast.success(`Welcome, ${ch.name}`);
      onDone(ch);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Onboarding failed");
      setNameChecked(false);
      setNameReason(e?.response?.data?.detail || "");
      setStep("name");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-[#050505] overflow-y-auto"
      data-testid="onboarding"
    >
      {/* Background flavour */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(60% 40% at 20% 10%, rgba(229,90,0,0.10), transparent 60%), " +
            "radial-gradient(50% 40% at 80% 90%, rgba(138,3,3,0.10), transparent 60%)",
        }}
      />
      <div className="relative max-w-md mx-auto px-4 py-8 space-y-6">
        <div className="text-center space-y-2">
          <div className="font-heading text-[10px] tracking-[0.4em] uppercase text-[#666]">
            Grimwalker
          </div>
          <div className="font-heading text-2xl tracking-[0.2em] uppercase text-[#E2DFD8]">
            {step === "name" ? "Name Thyself" : "Choose Your Path"}
          </div>
          <div className="font-body text-xs text-[#8c8c8c] italic px-6">
            {step === "name"
              ? "The road demands a name. Once given, others will remember it."
              : "Your first path shapes your body and blood. It can be changed later — for a price."}
          </div>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 font-mono text-[10px] tracking-widest uppercase">
          <span className={step === "name" ? "text-[#E55A00]" : "text-[#4A7BC7]"}>1 · Name</span>
          <span className="text-[#333]">—</span>
          <span className={step === "class" ? "text-[#E55A00]" : "text-[#333]"}>2 · Class</span>
        </div>

        {step === "name" && (
          <div className="panel p-4 space-y-3" data-testid="onboarding-name">
            <label className="font-heading text-[10px] tracking-[0.3em] uppercase text-[#8c8c8c]">
              Username (3–20 chars)
            </label>
            <div className="flex gap-2">
              <input
                data-testid="onboarding-name-input"
                value={name}
                onChange={(e) => { setName(e.target.value); setNameChecked(null); setNameReason(""); }}
                onKeyDown={(e) => e.key === "Enter" && checkName()}
                maxLength={20}
                placeholder="e.g. AshWraith"
                className="flex-1 bg-[#0a0a0a] border border-[#262626] px-3 py-2 text-sm text-[#E2DFD8] outline-none focus:border-[#B89C3A]"
              />
              <button
                data-testid="onboarding-name-check"
                onClick={checkName}
                disabled={checking || !name.trim()}
                className="px-3 border border-[#B89C3A] text-[#E5C34A] font-heading text-[10px] tracking-widest uppercase disabled:opacity-30"
              >
                {checking ? <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} /> : "Check"}
              </button>
            </div>

            {nameChecked === true && (
              <div className="flex items-center gap-2 font-body text-xs text-[#6E8B3D]" data-testid="onboarding-name-ok">
                <Check className="w-3 h-3" strokeWidth={2} /> Name accepted.
              </div>
            )}
            {nameChecked === false && (
              <div className="flex items-start gap-2 font-body text-xs text-[#E33333]" data-testid="onboarding-name-error">
                <X className="w-3 h-3 shrink-0 mt-0.5" strokeWidth={2} />
                <span>{nameReason}</span>
              </div>
            )}

            <div className="font-mono text-[9px] text-[#555] tracking-widest uppercase pt-2 border-t border-[#1a1a1a]">
              An AI warden reviews each name for offensive content.
            </div>

            <button
              data-testid="onboarding-name-next"
              onClick={() => setStep("class")}
              disabled={nameChecked !== true}
              className="w-full py-3 border border-[#E55A00] text-[#E55A00] font-heading text-xs tracking-widest uppercase disabled:opacity-20 flex items-center justify-center gap-2 hover:bg-[#E55A00]/10"
            >
              Continue <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
            </button>
          </div>
        )}

        {step === "class" && (
          <div className="space-y-3" data-testid="onboarding-class">
            {/* Sex toggle sits above the class list — the only trait
                that actually changes the chibi rendered on-screen. */}
            <div className="panel p-3 flex items-center justify-center gap-2" data-testid="onboarding-gender">
              <span className="font-heading text-[10px] tracking-[0.3em] uppercase text-[#8c8c8c] mr-2">
                Sex
              </span>
              {["male", "female"].map(g => (
                <button
                  key={g}
                  data-testid={`onboarding-gender-${g}`}
                  onClick={() => setAppearance(a => ({ ...a, gender: g }))}
                  className="px-3 py-1 border font-heading text-[10px] tracking-widest uppercase transition-colors"
                  style={{
                    borderColor: appearance.gender === g ? "#E55A00" : "#333",
                    color: appearance.gender === g ? "#E55A00" : "#8c8c8c",
                    background: appearance.gender === g ? "rgba(229,90,0,0.10)" : "transparent",
                  }}
                >
                  {g}
                </button>
              ))}
            </div>
            <div className="space-y-2">
              {CLASSES_MERGED.map((c) => {
                const Icon = c.Icon;
                const active = chosen === c.id;
                const chibi = classChibi(c.id, appearance?.gender, c.image);
                return (
                  <button
                    key={c.id}
                    data-testid={`onboarding-class-${c.id.toLowerCase()}`}
                    onClick={() => setChosen(c.id)}
                    className={`panel w-full p-3 text-left transition-colors ${active ? "border-2" : "border"}`}
                    style={{ borderColor: active ? c.tint : "#262626" }}
                  >
                    <div className="flex items-start gap-3">
                      {chibi ? (
                        <div
                          className="shrink-0 relative overflow-hidden"
                          style={{
                            width: 64, height: 96,
                            border: `1px solid ${c.tint}`,
                            background: `${c.tint}18`,
                          }}
                          data-testid={`class-chibi-${c.id.toLowerCase()}`}
                        >
                          <img
                            src={chibi}
                            alt={`${c.id} chibi`}
                            draggable={false}
                            className="pointer-events-none select-none"
                            style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "50% 20%" }}
                          />
                        </div>
                      ) : (
                        <div
                          className="w-10 h-10 flex items-center justify-center border shrink-0"
                          style={{ borderColor: c.tint, background: `${c.tint}18` }}
                        >
                          <Icon className="w-5 h-5" strokeWidth={1.5} style={{ color: c.tint }} />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Icon className="w-3 h-3 shrink-0" strokeWidth={1.5} style={{ color: c.tint }} />
                          <span className="font-heading text-sm tracking-widest uppercase" style={{ color: c.tint }}>
                            {c.id}
                          </span>
                          <span className="font-mono text-[9px] text-[#666] tracking-widest uppercase">
                            {c.tagline}
                          </span>
                        </div>
                        <div className="font-body text-xs text-[#B0ADA5] mt-1">{c.blurb}</div>
                        <div className="mt-2 grid grid-cols-6 gap-1 font-mono text-[9px]">
                          {Object.entries(c.stats).map(([k, v]) => (
                            <div key={k} className="panel-inner px-1 py-0.5 text-center">
                              <div className="text-[#8c8c8c] uppercase tracking-wider">{k.slice(0, 3)}</div>
                              <div className="text-[#E2DFD8] font-heading">{v}</div>
                            </div>
                          ))}
                        </div>
                        <div className="font-mono text-[9px] text-[#E5C34A] tracking-widest uppercase mt-1">
                          HP {c.hp}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="panel p-3 space-y-1" data-testid="onboarding-skill-hints">
              <div className="font-heading text-[10px] tracking-[0.3em] uppercase text-[#8c8c8c]">
                Skills sharpen stats
              </div>
              {SKILL_HINTS.map((s) => (
                <div key={s.label} className="font-mono text-[10px] text-[#8c8c8c]">
                  <span className="mr-2">{s.icon}</span>{s.label}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="onboarding-class-back"
                onClick={() => setStep("name")}
                className="py-3 border border-[#262626] text-[#8c8c8c] font-heading text-xs tracking-widest uppercase hover:text-[#E2DFD8]"
              >
                Back
              </button>
              <button
                data-testid="onboarding-commit"
                onClick={commit}
                disabled={!chosen || busy}
                className="py-3 border border-[#E55A00] text-[#E55A00] font-heading text-xs tracking-widest uppercase disabled:opacity-20 hover:bg-[#E55A00]/10 flex items-center justify-center gap-2"
              >
                {busy ? (
                  <><Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} /> Forging…</>
                ) : (
                  <>Awaken <ArrowRight className="w-3 h-3" strokeWidth={1.5} /></>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
